import { useRef, useEffect, useCallback } from 'react';
import {
  drawImage,
  setupHiDPICanvas,
  setupMaskCanvas,
  compositeMaskOverlay,
  exportMaskAsPng,
} from './canvas-renderer.js';
import { useCanvasInteraction } from '../hooks/use-canvas-interaction.js';
import { useEditorStore } from '../../../stores/editor.js';

export interface ViewportRef {
  offsetX: number;
  offsetY: number;
  scale: number;
}

export interface EditorCanvasHandle {
  setZoom: (scale: number) => void;
  fitToView: () => void;
  getViewport: () => ViewportRef;
  exportMask: () => string | null;
}

interface EditorCanvasProps {
  imageUrl: string;
  onImageError?: () => void;
  onZoomChange?: (scale: number) => void;
  onImageMeta?: (meta: { width: number; height: number }) => void;
  canvasHandleRef?: React.RefObject<EditorCanvasHandle | null>;
}

export function EditorCanvas({
  imageUrl,
  onImageError,
  onZoomChange,
  onImageMeta,
  canvasHandleRef,
}: EditorCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const innerContainerRef = useRef<HTMLDivElement>(null);
  const imageCanvasRef = useRef<HTMLCanvasElement>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const cursorCanvasRef = useRef<HTMLCanvasElement>(null);

  // Offscreen mask canvas for binary mask data (white = edit, transparent = preserve)
  const offscreenMaskRef = useRef<HTMLCanvasElement | null>(null);
  const offscreenMaskCtxRef = useRef<CanvasRenderingContext2D | null>(null);

  const imageRef = useRef<HTMLImageElement | null>(null);
  const dimensionsRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });

  // Viewport state for pan/zoom (useRef, not state — changes per-frame during gestures)
  const viewportRef = useRef<ViewportRef>({ offsetX: 0, offsetY: 0, scale: 1 });

  // Refresh the mask overlay after drawing
  const refreshOverlay = useCallback(() => {
    const overlayCanvas = overlayCanvasRef.current;
    const offscreenMaskCtx = offscreenMaskCtxRef.current;
    if (!overlayCanvas || !offscreenMaskCtx) return;
    const overlayCtx = overlayCanvas.getContext('2d');
    if (!overlayCtx) return;
    compositeMaskOverlay(offscreenMaskCtx, overlayCtx);
  }, []);

  // Apply viewport transform to the inner container (CSS transform for GPU-accelerated pan/zoom)
  const applyViewportTransform = useCallback(() => {
    const el = innerContainerRef.current;
    if (!el) return;
    const { offsetX, offsetY, scale } = viewportRef.current;
    el.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${scale})`;
  }, []);

  const onZoomChangeRef = useRef(onZoomChange);
  onZoomChangeRef.current = onZoomChange;
  const onImageMetaRef = useRef(onImageMeta);
  onImageMetaRef.current = onImageMeta;

  const handleViewportChange = useCallback(() => {
    applyViewportTransform();
    onZoomChangeRef.current?.(viewportRef.current.scale);
  }, [applyViewportTransform]);

  // Expose canvas handle for toolbar zoom controls
  useEffect(() => {
    if (!canvasHandleRef) return;
    (canvasHandleRef as React.MutableRefObject<EditorCanvasHandle | null>).current = {
      setZoom: (scale: number) => {
        viewportRef.current.scale = Math.max(0.25, Math.min(4, scale));
        handleViewportChange();
      },
      fitToView: () => {
        const container = containerRef.current;
        const dims = dimensionsRef.current;
        if (!container || dims.width === 0 || dims.height === 0) return;
        const containerRect = container.getBoundingClientRect();
        const padding = 32;
        const availW = containerRect.width - padding;
        const availH = containerRect.height - padding;
        const fitScale = Math.min(availW / dims.width, availH / dims.height, 1);
        // Center: compensate for scale shrinking from transform-origin 0,0
        // Flexbox centers the inner container at its original size; the offset
        // adjusts so the scaled image remains visually centered.
        const offsetX = (dims.width * (1 - fitScale)) / 2;
        const offsetY = (dims.height * (1 - fitScale)) / 2;
        viewportRef.current = { offsetX, offsetY, scale: fitScale };
        handleViewportChange();
      },
      getViewport: () => ({ ...viewportRef.current }),
      exportMask: () => {
        const maskCtx = offscreenMaskCtxRef.current;
        if (!maskCtx) return null;
        if (maskCtx.canvas.width === 0 || maskCtx.canvas.height === 0) return null;
        return exportMaskAsPng(maskCtx);
      },
    };
    return () => {
      if (canvasHandleRef) {
        (canvasHandleRef as React.MutableRefObject<EditorCanvasHandle | null>).current = null;
      }
    };
  }, [canvasHandleRef, handleViewportChange]);

  // Subscribe to maskData changes for undo/redo restore
  useEffect(() => {
    return useEditorStore.subscribe((state, prevState) => {
      // Only restore when maskData changes from undo/redo (object identity check)
      if (state.maskData && state.maskData !== prevState.maskData) {
        const maskCtx = offscreenMaskCtxRef.current;
        if (maskCtx) {
          maskCtx.putImageData(state.maskData, 0, 0);
          refreshOverlay();
        }
      }
    });
  }, [refreshOverlay]);

  // Set up canvas interaction (pointer events, drawing)
  useCanvasInteraction({
    cursorCanvasRef,
    offscreenMaskCtxRef,
    dimensionsRef,
    refreshOverlay,
    viewportRef,
    onViewportChange: handleViewportChange,
  });

  // Load image and initialize canvases
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      imageRef.current = img;
      const logicalWidth = img.naturalWidth;
      const logicalHeight = img.naturalHeight;
      const prevDims = dimensionsRef.current;
      dimensionsRef.current = { width: logicalWidth, height: logicalHeight };
      onImageMetaRef.current?.({ width: logicalWidth, height: logicalHeight });

      // Set up image canvas (Layer 0)
      const imageCanvas = imageCanvasRef.current;
      if (imageCanvas) {
        const ctx = setupHiDPICanvas(imageCanvas, logicalWidth, logicalHeight);
        if (ctx) {
          drawImage(ctx, img);
        }
      }

      // Set up offscreen mask canvas (binary mask data)
      const offscreenMask = document.createElement('canvas');
      offscreenMaskRef.current = offscreenMask;
      const maskCtx = setupMaskCanvas(offscreenMask, logicalWidth, logicalHeight);
      offscreenMaskCtxRef.current = maskCtx;

      // Iteration handoff (Keep editing): if the previous canvas had a mask
      // and the new image's logical dimensions match, restore the bitmap so
      // Generate stays enabled and the user can re-roll the same edit.
      // Drop it on dim change (rare now that the API picks an OpenAI output
      // size matching the source aspect — square stays square, etc.).
      const preservedMask = useEditorStore.getState().maskData;
      const dimsMatch = prevDims.width === logicalWidth && prevDims.height === logicalHeight;
      if (preservedMask && dimsMatch && maskCtx) {
        try {
          maskCtx.putImageData(preservedMask, 0, 0);
          // Mask is on the new offscreen ctx — repaint the visible overlay.
          requestAnimationFrame(() => refreshOverlay());
        } catch {
          // putImageData throws on dim mismatch we somehow missed; just drop.
          useEditorStore.getState().setMaskData(null);
        }
      } else if (preservedMask && !dimsMatch) {
        // New image is a different size — preserving the mask would place it
        // at the wrong coordinates. Drop it so hasMask returns false and the
        // user knows to draw a fresh one.
        useEditorStore.setState({ maskData: null, undoStack: [], redoStack: [] });
      }

      // Set up overlay canvas (Layer 1 — visible red tint)
      const overlayCanvas = overlayCanvasRef.current;
      if (overlayCanvas) {
        setupHiDPICanvas(overlayCanvas, logicalWidth, logicalHeight);
      }

      // Set up mask canvas placeholder (Layer 1 — same size, pointer-events: none)
      const maskCanvas = maskCanvasRef.current;
      if (maskCanvas) {
        setupHiDPICanvas(maskCanvas, logicalWidth, logicalHeight);
      }

      // Set up cursor canvas (Layer 2)
      const cursorCanvas = cursorCanvasRef.current;
      if (cursorCanvas) {
        setupHiDPICanvas(cursorCanvas, logicalWidth, logicalHeight);
      }

      // Fit inner container (the one with the viewport transform)
      const inner = innerContainerRef.current;
      if (inner) {
        inner.style.width = `${logicalWidth}px`;
        inner.style.height = `${logicalHeight}px`;
      }
    };

    img.onerror = () => {
      onImageError?.();
    };

    img.src = imageUrl;
  }, [imageUrl]);

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        padding: 'var(--space-3)',
      }}
      aria-label="Mask editor canvas"
    >
      <div
        ref={innerContainerRef}
        style={{
          position: 'relative',
          transformOrigin: '0 0',
        }}
        data-testid="canvas-container"
      >
        {/* Layer 0: Source image (read-only) */}
        <canvas
          ref={imageCanvasRef}
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
          data-testid="image-canvas"
        />
        {/* Layer 1: Mask overlay (red tint) */}
        <canvas
          ref={overlayCanvasRef}
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
          data-testid="overlay-canvas"
        />
        {/* Hidden: used only for display reference if needed */}
        <canvas
          ref={maskCanvasRef}
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', display: 'none' }}
          data-testid="mask-canvas"
        />
        {/* Layer 2: Cursor preview + receives pointer events */}
        <canvas
          ref={cursorCanvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            cursor: 'none',
            // touch-action: none stops the browser from claiming touchpad
            // gestures for page scroll/zoom before our pointer handlers see
            // them. Without this, Space+click+drag on a laptop trackpad
            // delivers a partial pointer sequence (or none at all) — the
            // browser intercepts the gesture as a scroll.
            touchAction: 'none',
          }}
          data-testid="cursor-canvas"
        />
      </div>
    </div>
  );
}
